import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable from "react-data-table-component";
import { useAuth0 } from '../../../../auth/useAuth0';
import ActionMenu from "../../../commons/action-menu/ActionMenu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faTrash } from "@fortawesome/free-solid-svg-icons";
import "./RolesPermissionComponent.css";
import { useAlert } from "../../../../context/alert/AlertContext";
import { useConfirm } from "../../../../context/confirm/ConfirmContext";
import LoadingComponent from "../../../commons/LoadingComponent";
import ActionsButtonsComponent from "../../../commons/action-buttons/ActionsButtonsComponent";
import { RoleBusiness } from "../../../../model/role/RoleBusiness";
import { Permission } from "../../../../model/permission/Permission";
import { TeamMember } from "../../../../model/teammember/TeamMember";
import { resolveTeamMemberColor, readableTextColor } from "../../../../util/teamMemberColor";
import { getBusinessRoles, saveBusinessRole, deleteBusinessRole } from "../../../../service/role/business-role-service";
import { getAllPermissions } from "../../../../service/permission/permission-service";
import { getTeamMembersByBusinessId, saveTeamMember } from "../../../../service/teammember/team-member-service";
import { RolePermissionBusinessInput } from "../../../../model/role/RoleBusiness";
import { useBusinessContext } from "../../../../context/BusinessContext";
import { useNavigation } from "../../../../context/NavigationContext";
import { MENU } from "../../../util/Utils";

const RolesPermissionComponent: React.FC = () => {
    const { t } = useTranslation();
    const confirmDialog = useConfirm();
    const { getAccessTokenSilently } = useAuth0();
    const { setAlert } = useAlert();
    const { selectedBusiness } = useBusinessContext();
    const { setActiveMenu, setPendingTeamMemberId } = useNavigation();
    const businessIdStr = selectedBusiness?.id ? String(selectedBusiness.id) : "";

    const [selectedRole, setSelectedRole] = useState<string | null>(null);
    const [permissionsData, setPermissionsData] = useState<
        { permission: string; actions: { read: boolean; update: boolean; create: boolean; delete: boolean } }[]
    >([]);
    const [isAddingNewRole, setIsAddingNewRole] = useState<boolean>(false);
    const [deletingRoleId, setDeletingRoleId] = useState<string | null>(null);


    // Slice 1: roles come from backend (getAllRoles).
    // Slice 2: permissions catalog + permission matrix per role from backend (read-only).
    // Users panel is still mocked — wired in a later slice.
    const [roles, setRoles] = useState<RoleBusiness[]>([]);
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
    const [loadingRoles, setLoadingRoles] = useState<boolean>(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const [rolesData, permsData, tmData] = await Promise.all([
                    businessIdStr
                        ? getBusinessRoles(businessIdStr, token)
                        : Promise.resolve([] as RoleBusiness[]),
                    getAllPermissions(token),
                    businessIdStr
                        ? getTeamMembersByBusinessId(businessIdStr, token)
                        : Promise.resolve([] as TeamMember[]),
                ]);
                if (!cancelled) {
                    const sortedRoles = [...rolesData].sort((a, b) =>
                        (a.name ?? "").localeCompare(b.name ?? "")
                    );
                    setRoles(sortedRoles);
                    setPermissions(permsData);
                    setTeamMembers(tmData);
                }
            } catch (e) {
                if (!cancelled) {
                    setAlert(
                        "danger",
                        e instanceof Error ? e.message : t('settings.roles.alerts.loadFailed')
                    );
                }
            } finally {
                if (!cancelled) setLoadingRoles(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [getAccessTokenSilently, setAlert, businessIdStr]);

    // Derived: team members for the currently selected role. The "image" is the
    // member's initials over their calendar color (same pattern as the Equipe
    // screen) — members have no photo field.
    const usersForSelectedRole = teamMembers
        .filter((tm) => selectedRole && String(tm.roleBusinessId) === String(selectedRole))
        .map((tm) => {
            const idx = teamMembers.findIndex((x) => x.id === tm.id);
            return {
                id: tm.id,
                name: `${tm.firstName} ${tm.lastName}`,
                email: tm.email ?? '',
                initials: ((tm.firstName?.[0] ?? '?') + (tm.lastName?.[0] ?? '')).toUpperCase(),
                color: resolveTeamMemberColor(tm, idx),
                member: tm,
            };
        });

    const handleViewTeamMember = (tmId: string) => {
        setPendingTeamMemberId(tmId);
        setActiveMenu(MENU.TEAM_MEMBER);
    };

    const handleRemoveFromRole = async (tm: TeamMember) => {
        if (
            !(await confirmDialog({
                title: t('common.confirmTitle'),
                message: t('settings.roles.removeFromRoleConfirm', {
                    name: `${tm.firstName} ${tm.lastName}`,
                    role: selectedRoleObj?.name ?? '',
                }),
                danger: true,
                confirmLabel: t('settings.roles.removeFromRole'),
            }))
        ) {
            return;
        }
        try {
            const token = await getAccessTokenSilently();
            await saveTeamMember(
                {
                    id: tm.id,
                    firstName: tm.firstName,
                    lastName: tm.lastName,
                    email: tm.email,
                    phone: tm.phone,
                    businessId: String(tm.businessId),
                    allowOnlineBooking: tm.allowOnlineBooking,
                    hasAppAccess: tm.hasAppAccess,
                    roleId: tm.roleId ?? null,
                    roleBusinessId: null,
                    workingHours: (tm.workingHours ?? []).map((wh) => ({
                        id: wh.id,
                        dayOfWeek: wh.dayOfWeek,
                        startTime: wh.startTime,
                        endTime: wh.endTime,
                        allowBookingOnIntervalTime: wh.allowBookingOnIntervalTime,
                        startIntervalTime: wh.startIntervalTime ?? null,
                        endIntervalTime: wh.endIntervalTime ?? null,
                    })),
                    serviceIds: tm.serviceIds ?? [],
                },
                token
            );
            setTeamMembers((prev) =>
                prev.map((t) => (String(t.id) === String(tm.id) ? { ...t, roleBusinessId: null } : t))
            );
            setAlert("success", t('settings.roles.alerts.removedFromRole', { name: `${tm.firstName} ${tm.lastName}` }));
        } catch (e) {
            setAlert(
                "danger",
                e instanceof Error ? e.message : t('settings.roles.alerts.removeFailed')
            );
        }
    };

    // Selected role + editable matrix state.
    const selectedRoleObj = roles.find((r) => String(r.id) === String(selectedRole)) ?? null;
    type MatrixState = Record<string, { selectedOption?: string; isActive?: boolean }>;
    const [matrix, setMatrix] = useState<MatrixState>({});
    /** Snapshot of the matrix as loaded — used to detect "is modified". */
    const [originalMatrix, setOriginalMatrix] = useState<MatrixState>({});
    const [savingMatrix, setSavingMatrix] = useState(false);
    const [newRoleName, setNewRoleName] = useState<string>("");
    /** Editable name of the selected role (rename — the CRUD was create/delete only). */
    const [editedRoleName, setEditedRoleName] = useState<string>("");

    /**
     * Dirty when the matrix OR the role name differs from the loaded snapshot.
     * Compared shallowly via JSON.stringify since the state is small.
     */
    const isMatrixModified =
        JSON.stringify(matrix) !== JSON.stringify(originalMatrix)
        || (selectedRoleObj != null && editedRoleName.trim() !== (selectedRoleObj.name ?? ""));

    // Re-init matrix + editable name when the selected role changes.
    useEffect(() => {
        if (!selectedRoleObj) {
            setMatrix({});
            setOriginalMatrix({});
            setEditedRoleName("");
            return;
        }
        const next: MatrixState = {};
        for (const rp of selectedRoleObj.permissions ?? []) {
            next[String(rp.permissionId)] = {
                selectedOption: rp.selectedOption ?? undefined,
                isActive: rp.isActive ?? undefined,
            };
        }
        setMatrix(next);
        setOriginalMatrix(next);
        setEditedRoleName(selectedRoleObj.name ?? "");
    }, [selectedRoleObj]);

    /** Discard any in-flight edits and reset to the loaded snapshot. */
    const handleDiscardMatrix = () => {
        setMatrix(originalMatrix);
        if (selectedRoleObj) setEditedRoleName(selectedRoleObj.name ?? "");
    };

    const updateMatrix = (
        permId: string,
        patch: { selectedOption?: string; isActive?: boolean }
    ) => {
        setMatrix((prev) => ({
            ...prev,
            [permId]: { ...(prev[permId] ?? {}), ...patch },
        }));
    };

    const handleSavePermissions = async () => {
        if (!selectedRoleObj) return;
        const nameToSave = editedRoleName.trim();
        if (!nameToSave) {
            setAlert("danger", t('settings.roles.alerts.nameRequired'));
            return;
        }
        // Only "configured" permissions go to the input. The backend deletes
        // any existing row for this role whose permissionId isn't in the list.
        try {
            setSavingMatrix(true);
            const token = await getAccessTokenSilently();
            const saved = await saveBusinessRole(
                {
                    id: selectedRoleObj.id,
                    businessId: selectedRoleObj.businessId,
                    name: nameToSave,
                    permissions: buildPermissionsInput(),
                },
                token
            );
            setAlert("success", t('settings.roles.alerts.permissionsSaved', { name: saved.name }));
            setRoles((prev) => {
                const next = prev.map((r) =>
                    String(r.id) === String(saved.id) ? saved : r
                );
                return next.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
            });
            // Mark the current matrix as the new clean baseline so the
            // unsaved-changes banner disappears after a successful save.
            setOriginalMatrix(matrix);
        } catch (e) {
            setAlert(
                "danger",
                e instanceof Error ? e.message : t('settings.roles.alerts.saveFailed')
            );
        } finally {
            setSavingMatrix(false);
        }
    };

    const handleRoleClick = (roleId: string) => {
        setSelectedRole(roleId);
        setIsAddingNewRole(false);
    };

    /** Delete a role (after confirm). Blocked when team members are still assigned
     *  to it — the user must reassign them first (avoids orphaning members). */
    const handleDeleteRole = async (role: RoleBusiness, e: React.MouseEvent) => {
        e.stopPropagation(); // don't also select the role
        if (deletingRoleId) return; // anti-double-click: one delete at a time
        const assigned = teamMembers.filter(
            (tm) => String(tm.roleBusinessId) === String(role.id)
        );
        if (assigned.length > 0) {
            setAlert('danger', t('settings.roles.deleteHasMembers', { count: assigned.length }));
            return;
        }
        if (!(await confirmDialog({ title: t('settings.roles.deleteTitle'), message: t('settings.roles.deleteConfirm', { name: role.name }), danger: true, confirmLabel: t('common.delete') }))) return;
        setDeletingRoleId(String(role.id));
        try {
            const token = await getAccessTokenSilently();
            const ok = await deleteBusinessRole(businessIdStr, String(role.id), token);
            if (ok) {
                setRoles((prev) => prev.filter((r) => String(r.id) !== String(role.id)));
                if (String(selectedRole) === String(role.id)) setSelectedRole(null);
                setAlert('success', t('settings.roles.deleteSuccess'));
            } else {
                setAlert('danger', t('settings.roles.deleteError'));
            }
        } catch (err) {
            setAlert('danger', err instanceof Error ? err.message : t('settings.roles.deleteError'));
        } finally {
            setDeletingRoleId(null);
        }
    };

    const handleAddNewRoleClick = () => {
        setIsAddingNewRole(true);
        setSelectedRole(null);
        setNewRoleName("");
        setMatrix({});
    };

    const buildPermissionsInput = (): RolePermissionBusinessInput[] => {
        // Never persist WAITLIST without CALENDAR access — it's a calendar
        // sub-permission. Drop it when Calendar is unset/NO_ACCESS so the saved
        // matrix can't carry an orphan waitlist grant.
        const calPerm = permissions.find((p) => p.permissionKey === "CALENDAR");
        const calVal = calPerm ? matrix[String(calPerm.id)]?.selectedOption : undefined;
        const calendarHasAccess = !!calVal && calVal !== "NO_ACCESS";
        const waitlistId = permissions.find((p) => p.permissionKey === "WAITLIST")?.id;
        return Object.entries(matrix)
            .filter(
                ([, v]) =>
                    v.isActive === true ||
                    (typeof v.selectedOption === "string" && v.selectedOption !== "")
            )
            .filter(([permissionId]) =>
                calendarHasAccess || String(permissionId) !== String(waitlistId)
            )
            .map(([permissionId, v]) => ({
                permissionId,
                selectedOption: v.selectedOption ?? null,
                isActive: typeof v.isActive === "boolean" ? v.isActive : null,
            }));
    };

    const handleSaveNewRole = async () => {
        if (!newRoleName.trim()) {
            setAlert("danger", t('settings.roles.alerts.nameRequired'));
            return;
        }
        if (!businessIdStr) {
            setAlert("danger", t('settings.roles.alerts.selectBusinessFirst'));
            return;
        }
        try {
            setSavingMatrix(true);
            const token = await getAccessTokenSilently();
            const saved = await saveBusinessRole(
                {
                    businessId: businessIdStr,
                    name: newRoleName.trim(),
                    permissions: buildPermissionsInput(),
                },
                token
            );
            setAlert("success", t('settings.roles.alerts.created', { name: saved.name }));
            setRoles((prev) =>
                [...prev, saved].sort((a, b) =>
                    (a.name ?? "").localeCompare(b.name ?? "")
                )
            );
            setIsAddingNewRole(false);
            setSelectedRole(saved.id);
        } catch (e) {
            setAlert(
                "danger",
                e instanceof Error ? e.message : t('settings.roles.alerts.createFailed')
            );
        } finally {
            setSavingMatrix(false);
        }
    };

    const renderPermissionRow = (perm: Permission) => {
        const current = matrix[perm.id] ?? {};
        const isReports = perm.permissionKey === "REPORTS";
        const isCalendar = perm.permissionKey === "CALENDAR";
        const isWaitlist = perm.permissionKey === "WAITLIST";
        // Every MULTI_LEVEL cell is explicit: unconfigured shows t('settings.roles.options.NO_ACCESS')
        // — there is no ambiguous "No setting" that silently denies.
        // REPORTS is the one exception: blank means "inherit the Calendar scope".
        return (
            <tr key={perm.id}>
                <td>
                    {/* Catalog labels/descriptions localized in the UI — the DB
                        stores them in PT only; unknown keys fall back to it. */}
                    <div>{t(`settings.roles.catalog.${perm.permissionKey}.label`, { defaultValue: perm.name })}</div>
                    {perm.description && (
                        <div className="text-muted small" style={{ maxWidth: 480 }}>
                            {t(`settings.roles.catalog.${perm.permissionKey}.desc`, { defaultValue: perm.description })}
                        </div>
                    )}
                    {isCalendar && (
                        <div className="text-muted small fst-italic" style={{ maxWidth: 480 }}
                            dangerouslySetInnerHTML={{ __html: t('settings.roles.calendarHint') }}
                        />
                    )}
                    {isWaitlist && (
                        <div className="text-muted small fst-italic" style={{ maxWidth: 480 }}
                            dangerouslySetInnerHTML={{ __html: t('settings.roles.waitlistHint') }}
                        />
                    )}
                    {isReports && (
                        <div className="text-muted small fst-italic" style={{ maxWidth: 480 }}
                            dangerouslySetInnerHTML={{ __html: t('settings.roles.reportsHint') }}
                        />
                    )}
                </td>
                <td>
                    {perm.type === "BOOLEAN" ? (
                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                data-cy={`roles-permission-${(perm.permissionKey ?? perm.id).toLowerCase().replace(/_/g, '-')}-toggle`}
                                checked={current.isActive === true}
                                onChange={(e) =>
                                    updateMatrix(perm.id, { isActive: e.target.checked })
                                }
                            />
                            <label className="form-check-label small">
                                {current.isActive ? t('settings.roles.enabled') : t('settings.roles.disabled')}
                            </label>
                        </div>
                    ) : (
                        <select
                            className="form-select form-select-sm"
                            data-cy={`roles-permission-${(perm.permissionKey ?? perm.id).toLowerCase().replace(/_/g, '-')}-select`}
                            value={current.selectedOption ?? (isReports ? "" : (perm.options?.[0]?.optionName ?? "NO_ACCESS"))}
                            onChange={(e) =>
                                updateMatrix(perm.id, { selectedOption: e.target.value })
                            }
                        >
                            {/* Only REPORTS keeps a blank option (= inherit Agenda). */}
                            {isReports && <option value="">{t('settings.roles.followsCalendar')}</option>}
                            {(perm.options ?? []).map((o) => (
                                <option key={o.id} value={o.optionName}>
                                    {t(`settings.roles.options.${o.optionName}`, { defaultValue: o.optionName })}
                                </option>
                            ))}
                        </select>
                    )}
                </td>
            </tr>
        );
    };

    const renderMatrixRows = (): React.ReactNode[] => {
        // Catalog order (displayOrder), with a section header each time the
        // group changes — the backend owns grouping via permission.groupName.
        const sorted = [...permissions].sort(
            (a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999)
        );
        // Waitlist depends on Calendar: only show its row when the current role's
        // CALENDAR cell grants access (unset/NO_ACCESS = hidden). Mirrors the
        // backend, which seeds/keeps WAITLIST only for calendar-enabled roles.
        const calPerm = permissions.find((p) => p.permissionKey === "CALENDAR");
        const calVal = calPerm ? matrix[String(calPerm.id)]?.selectedOption : undefined;
        const calendarHasAccess = !!calVal && calVal !== "NO_ACCESS";
        const rows: React.ReactNode[] = [];
        let lastGroup: string | null = null;
        for (const perm of sorted) {
            if (perm.permissionKey === "WAITLIST" && !calendarHasAccess) continue;
            const group = perm.groupName ?? "OTHER";
            if (group !== lastGroup) {
                lastGroup = group;
                rows.push(
                    <tr key={`group-${group}`} className="table-light">
                        <td
                            colSpan={2}
                            className="fw-semibold text-uppercase small text-muted py-2"
                        >
                            {t(`settings.roles.groups.${group}`, { defaultValue: group })}
                        </td>
                    </tr>
                );
            }
            rows.push(renderPermissionRow(perm));
        }
        return rows;
    };

    const usersColumns = [
        {
            // Single member column: initials avatar + stacked name/email (same
            // cell layout as the Equipe table) — no separate image column.
            name: t('settings.roles.usersTitle'),
            grow: 1,
            cell: (row: { initials: string; color: string; name: string; email: string }) => (
                <div className="d-flex align-items-center gap-2 py-1">
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{
                            width: '40px',
                            height: '40px',
                            background: row.color,
                            color: readableTextColor(row.color),
                            fontWeight: 700,
                            fontSize: '0.9rem',
                        }}
                    >
                        {row.initials}
                    </div>
                    <div className="d-flex flex-column">
                        <span>{row.name}</span>
                        {row.email && (
                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>{row.email}</small>
                        )}
                    </div>
                </div>
            ),
        },
        {
            name: "",
            width: '70px',
            right: true,
            cell: (row: { id: string; name: string; member: TeamMember }) => (
                <ActionMenu
                    items={[
                        { label: t('common.edit'), onClick: () => handleViewTeamMember(row.id) },
                        {
                            label: t('common.remove'),
                            danger: true,
                            onClick: () => handleRemoveFromRole(row.member),
                        },
                    ]}
                />
            ),
        },
    ];

    return (
        <div className="roles-permission-page" data-cy="roles-page">

            <div className="d-flex">
                {/* Left Section */}
                <div className="roles-list" style={{ flex: 1, padding: "1rem", borderRight: "1px solid #ddd" }}>
                    {/* "Roles List" title removed — it's implicit; the button alone
                       takes its place (per UX feedback). */}
                    <div className="mb-3">
                        <button className="btn btn-primary w-100" data-cy="roles-add-btn" onClick={handleAddNewRoleClick}>
                            {t('settings.roles.addNewRole')}
                        </button>
                    </div>

                    {loadingRoles && <LoadingComponent />}
                    {!loadingRoles && roles.length === 0 && (
                        <div className="text-muted fst-italic p-2">{t('settings.roles.noRolesFound')}</div>
                    )}

                    {roles.map((role) => (
                        <div
                            key={role.id}
                            data-cy={`roles-row-${role.id}`}
                            className={`card mb-3 ${selectedRole === role.id ? "border-primary" : ""}`}
                            onClick={() => handleRoleClick(role.id)}
                            style={{ cursor: "pointer" }}
                        >
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="d-inline-flex align-items-center gap-2">
                                        {/* Neutral "group of members" badge beside the label —
                                            a role is a group, not a person. */}
                                        <span
                                            className="rounded-circle d-inline-flex align-items-center justify-content-center"
                                            style={{
                                                width: "26px",
                                                height: "26px",
                                                background: "var(--gs-primary-soft, #efeefc)",
                                                color: "var(--gs-primary, #7266e8)",
                                            }}
                                            aria-hidden="true"
                                        >
                                            <FontAwesomeIcon icon={faUsers} style={{ fontSize: "0.72rem" }} />
                                        </span>
                                        <h6 className="text-muted mb-0">{t('settings.roles.roleLabel')}</h6>
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger d-inline-flex align-items-center"
                                        data-cy={`roles-delete-${role.id}`}
                                        title={t('settings.roles.deleteTitle')}
                                        aria-label={t('settings.roles.deleteTitle')}
                                        onClick={(e) => handleDeleteRole(role, e)}
                                    >
                                        <FontAwesomeIcon icon={faTrash} style={{ fontSize: "0.8rem" }} />
                                    </button>
                                </div>
                                <h5>{role.name}</h5>
                            </div>
                        </div>
                    ))}
                </div>

            {/* Right Section */}
            <div className="permissions-users" style={{ flex: 2, padding: "1rem" }}>
                {isAddingNewRole ? (
                    <>
                    {/* Same unsaved-changes banner used everywhere else (Settings →
                        Business, role permissions). Replaces the inline Cancel/Save
                        Role buttons so the create flow matches the rest of the app. */}
                    <ActionsButtonsComponent
                        /* Derived dirty (§5.10): the banner shows only once the user
                           actually typed a name or touched the matrix — not the moment
                           the empty "Add role" form opens. */
                        isModified={(newRoleName.trim() !== "" || Object.keys(matrix).length > 0) && !savingMatrix}
                        onSave={handleSaveNewRole}
                        onCancel={() => {
                            setIsAddingNewRole(false);
                            setNewRoleName("");
                            setMatrix({});
                        }}
                    />
                    <div className="card mb-4">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">{t('settings.roles.addNewRoleTitle')}</h5>
                                {savingMatrix && (
                                    <span className="text-muted small">{t('settings.roles.saving')}</span>
                                )}
                            </div>
                            <div className="mb-3">
                                <label className="form-label">{t('settings.roles.roleName')}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    data-cy="roles-name-input"
                                    maxLength={100}
                                    value={newRoleName}
                                    onChange={(e) => setNewRoleName(e.target.value)}
                                />
                            </div>
                            <h6>{t('settings.roles.permissionsTitle')}</h6>
                            {permissions.length === 0 ? (
                                <p className="text-muted fst-italic mb-0">
                                    {t('settings.roles.noPermissions')}
                                </p>
                            ) : (
                                <table className="table align-middle gs-perm-matrix">
                                    <thead>
                                        <tr>
                                            <th>{t('settings.roles.permissionCol')}</th>
                                            <th style={{ width: 220 }}>{t('settings.roles.settingCol')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>{renderMatrixRows()}</tbody>
                                </table>
                            )}
                        </div>
                    </div>
                    </>
                ) : selectedRole ? (
                    <>
                        {/* Unified unsaved-changes banner — same pattern used
                            in Settings → Business. Banner appears at the top
                            with Discard + Save when the matrix is dirty. */}
                        <ActionsButtonsComponent
                            isModified={isMatrixModified && !savingMatrix}
                            onSave={handleSavePermissions}
                            onCancel={handleDiscardMatrix}
                        />
                        <div className="card mb-4">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    {/* Editable role name (rename — CRUD was create/delete only). */}
                                    <div className="flex-grow-1 me-3">
                                        <label className="form-label">{t('settings.roles.roleName')}</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            data-cy="roles-edit-name-input"
                                            maxLength={100}
                                            value={editedRoleName}
                                            onChange={(e) => setEditedRoleName(e.target.value)}
                                        />
                                    </div>
                                    {savingMatrix && (
                                        <span className="text-muted small">{t('settings.roles.saving')}</span>
                                    )}
                                </div>

                                {permissions.length === 0 ? (
                                    <p className="text-muted fst-italic mb-0">
                                        {t('settings.roles.noPermissions')}
                                    </p>
                                ) : (
                                    <table className="table align-middle gs-perm-matrix">
                                        <thead>
                                            <tr>
                                                <th>{t('settings.roles.permissionCol')}</th>
                                                <th style={{ width: 220 }}>{t('settings.roles.settingCol')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>{renderMatrixRows()}</tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                        <div className="card">
                            <div className="card-body">
                                <h5>{t('settings.roles.usersTitle')}</h5>
                                {usersForSelectedRole.length === 0 ? (
                                    <p className="text-muted fst-italic mb-0">
                                        {t('settings.roles.noMembers')}
                                    </p>
                                ) : (
                                    <DataTable
                                        columns={usersColumns}
                                        data={usersForSelectedRole}
                                        noHeader
                                        pagination
                                    />
                                )}
                            </div>
                        </div>
                    </>
                ) : (
                    <p>{t('settings.roles.selectRole')}</p>
                )}
                </div>
            </div>
        </div>
    );
};

export default RolesPermissionComponent;
